<template>
  <div>
    <p class="body-2 text--secondary mb-4">
      Busque e vincule designações ao provedor
      <strong>{{ provedor.nome }}</strong>.
    </p>

    <v-autocomplete
      v-model="designacaoSelecionada"
      :items="designacoesBusca"
      item-text="label"
      item-value="id"
      return-object
      label="Buscar designação"
      placeholder="Digite pelo menos 3 letras"
      outlined
      dense
      no-filter
      clearable
      :loading="carregandoBusca"
      @update:search-input="buscarDesignacoes"
      @change="adicionarDesignacao"
    />

    <div class="mt-4">
      <h6 class="mb-2">Designações vinculadas ({{ designacoesVinculadas.length }})</h6>

      <SGPAlertaComponent v-if="!designacoesVinculadas.length && !carregandoVinculadas">
        Nenhuma designação vinculada a este provedor.
      </SGPAlertaComponent>

      <div v-else class="d-flex flex-wrap">
        <v-chip
          v-for="item in designacoesVinculadas"
          :key="item.id"
          class="ma-1"
          close
          color="primary"
          text-color="white"
          @click:close="removerDesignacao(item.id)"
        >
          {{ item.label }}
        </v-chip>
      </div>
    </div>

    <div class="container-botoes mt-4">
      <button
        class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--default primary text-capitalize"
        type="button"
        :disabled="salvando || carregandoVinculadas"
        @click="salvar"
      >
        {{ salvando ? 'Salvando...' : 'Salvar' }}
      </button>
      <button
        class="v-btn v-btn--outlined v-btn--rounded theme--light v-size--default grey-5--text text-capitalize"
        type="button"
        :disabled="salvando"
        @click="$emit('fechar')"
      >
        Cancelar
      </button>
    </div>

    <SGPLoadingComponent v-if="carregandoVinculadas || salvando" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'VincularDesignacoesProvedorComponent',

  props: {
    provedor: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      designacoesVinculadas: [],
      designacoesBusca: [],
      designacaoSelecionada: null,
      carregandoVinculadas: false,
      carregandoBusca: false,
      salvando: false,
      timeoutBusca: null
    }
  },

  async created () {
    await this.carregarVinculadas()
  },

  beforeDestroy () {
    if (this.timeoutBusca) {
      clearTimeout(this.timeoutBusca)
    }
  },

  methods: {
    ...mapActions('ctrlProvedores', [
      'buscarProvedorComDesignacoes',
      'sincronizarDesignacoes'
    ]),
    ...mapActions('ctrlDesignacao', ['pesquisarDesignacoes']),

    normalizarDesignacao (item) {
      if (!item) return null

      const id = item.id ?? item.ctrl_designacao_id
      if (!id) return null

      const designacao = item.designacao || item.acesso_lp || item.nome || `#${id}`
      const acesso = item.acesso_lp && item.acesso_lp !== designacao ? ` · ${item.acesso_lp}` : ''

      return {
        id,
        designacao: item.designacao || '',
        acesso_lp: item.acesso_lp || '',
        label: `${designacao}${acesso}`
      }
    },

    extrairDesignacoes (payload) {
      if (!payload) return []

      if (Array.isArray(payload)) {
        return payload
      }

      if (Array.isArray(payload.designacoes)) {
        return payload.designacoes
      }

      if (Array.isArray(payload.data)) {
        return payload.data
      }

      if (Array.isArray(payload.data?.designacoes)) {
        return payload.data.designacoes
      }

      return []
    },

    async carregarVinculadas () {
      if (!this.provedor?.id) return

      this.carregandoVinculadas = true
      try {
        const resposta = await this.buscarProvedorComDesignacoes(this.provedor.id)
        this.designacoesVinculadas = this.extrairDesignacoes(resposta)
          .map(this.normalizarDesignacao)
          .filter(Boolean)
      } catch (error) {
        console.error('Erro ao carregar designações do provedor:', error)
        this.designacoesVinculadas = []
      } finally {
        this.carregandoVinculadas = false
      }
    },

    buscarDesignacoes (termo) {
      if (this.timeoutBusca) {
        clearTimeout(this.timeoutBusca)
      }

      if (!termo || termo.length < 3) {
        this.designacoesBusca = []
        return
      }

      this.timeoutBusca = setTimeout(async () => {
        this.carregandoBusca = true
        try {
          const idsVinculados = this.designacoesVinculadas.map(item => item.id)
          const resultado = await this.pesquisarDesignacoes({
            page: 1,
            current_page: 1,
            per_page: 50,
            designacao: termo
          })

          this.designacoesBusca = (resultado || [])
            .map(this.normalizarDesignacao)
            .filter(Boolean)
            .filter(item => !idsVinculados.includes(item.id))
        } catch (error) {
          console.error('Erro ao buscar designações:', error)
          this.designacoesBusca = []
        } finally {
          this.carregandoBusca = false
        }
      }, 300)
    },

    adicionarDesignacao (item) {
      if (!item || !item.id) {
        this.designacaoSelecionada = null
        return
      }

      const jaVinculada = this.designacoesVinculadas.some(designacao => designacao.id === item.id)
      if (!jaVinculada) {
        this.designacoesVinculadas.push(this.normalizarDesignacao(item))
      }

      this.designacaoSelecionada = null
      this.designacoesBusca = this.designacoesBusca.filter(designacao => designacao.id !== item.id)
    },

    removerDesignacao (id) {
      this.designacoesVinculadas = this.designacoesVinculadas.filter(item => item.id !== id)
    },

    async salvar () {
      if (!this.provedor?.id || this.salvando) return

      this.salvando = true
      try {
        await this.sincronizarDesignacoes({
          id: this.provedor.id,
          designacoes: this.designacoesVinculadas.map(item => item.id)
        })
        this.$emit('salvo')
      } catch (error) {
        console.error('Erro ao sincronizar designações:', error)
      } finally {
        this.salvando = false
      }
    }
  }
}
</script>
